import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  Popover,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import { useAuth } from '../../../../util/authenticationProvider';
import { useTranslation } from 'react-i18next';

const UserBoxButton = styled(Button)(
    ({ theme }) => `
    padding-left: ${theme.spacing(1)};
    padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
    ({ theme }) => `
    background: ${theme.colors?.alpha?.black?.[5] || theme.palette.action.hover};
    padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
    ({ theme }) => `
    text-align: left;
    padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
    ({ theme }) => `
    font-weight: ${theme.typography.fontWeightBold};
    color: ${theme.palette.secondary.main};
    display: block;
`
);

const UserBoxDescription = styled(Typography)(
    ({ theme }) => `
    color: ${lighten(theme.palette.secondary.main, 0.5)};
`
);

function HeaderUserbox() {
  const { account, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const ref = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setOpen] = useState(false);

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);

  const firstname = account?.profile?.firstname ?? '';
  const lastname = account?.profile?.lastname ?? '';
  const userName = firstname || lastname || 'User';
  const avatarUrl = '/static/images/avatars/1.jpg';

  const getUserRoleLabel = (): string => {
    const roles = account?.roles ?? [];
    if (Array.isArray(roles)) {
      if (roles.includes('tenant-admin') || roles.includes('tenan-admin')) return t('roles.admin');
      if (roles.includes('tenant-employee') || roles.includes('tenat-employee')) return t('roles.employee');
    } else if (typeof roles === 'string') {
      if (roles === 'tenant-admin' || roles === 'tenan-admin') return t('roles.admin');
      if (roles === 'tenant-employee' || roles === 'tenat-employee') return t('roles.employee');
    }

    return t('roles.user');
  };

  const handleLogOut = () => {
    logout();
    navigate('/cp/login');
  };

  return (
      <>
        <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
          {/* <Avatar variant="rounded" alt={userName} src={avatarUrl} /> */}
          <Hidden mdDown>
            <UserBoxText>
              <UserBoxLabel variant="body1">{userName}</UserBoxLabel>
              <UserBoxDescription variant="body2">
                {getUserRoleLabel()}
              </UserBoxDescription>
            </UserBoxText>
          </Hidden>
          <Hidden smDown>
            <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
          </Hidden>
        </UserBoxButton>

        <Popover
            anchorEl={ref.current}
            onClose={handleClose}
            open={isOpen}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
        >
          <MenuUserBox sx={{ minWidth: 210 }} display="flex">
            <Avatar variant="rounded" alt={userName} src={avatarUrl} />
            <UserBoxText>
              <UserBoxLabel variant="body1">{userName}</UserBoxLabel>
              <UserBoxDescription variant="body2">
                {getUserRoleLabel()}
              </UserBoxDescription>
            </UserBoxText>
          </MenuUserBox>

          <Divider />

          <Box sx={{ m: 1 }}>
            <Button color="primary" fullWidth onClick={handleLogOut}>
              <LockOpenTwoToneIcon sx={{ mr: 1 }} />
              Sign out
            </Button>
          </Box>
        </Popover>
      </>
  );
}

export default HeaderUserbox;
